import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UserTypeSelector } from '../components/UserTypeSelector';
import { BillUpload } from '../components/BillUpload';
import { EnergyProfileAgricultural } from '../components/EnergyProfileAgricultural';
import { ContactForm } from '../components/ContactForm';
import { OTPVerification } from '../components/OTPVerification';
import { LocationSelector } from '../components/LocationSelector';
import { ProjectSummary } from '../components/ProjectSummary';
import { SolarDesignView, type SolarDesignPersistExtras } from '../components/SolarDesignView';
import { EnergyConsumption } from '../components/EnergyConsumption';
import { SiteOperations } from '../components/SiteOperations';
import { AgriDesignRequirements } from '../components/AgriDesignRequirements';
import { ConfirmationScreen } from '../components/ConfirmationScreen';
import { StepIndicator } from '../components/StepIndicator';
import { ProjectDetailsView } from '../components/ProjectDetailsView';
import { LoginModal } from '../components/LoginModal';
import { ProjectLimitModal } from '../components/ProjectLimitModal';
import { SystemSizeLimitModal } from '../components/SystemSizeLimitModal';
import { WelcomeScreen } from '../components/WelcomeScreen';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { useAuthStore } from '../store/authStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import type { Step, UserType, BillData, LocationData, ContactInfo, EnergyConsumptionData, SolarDesign, FinancialData } from '../types';
import type { BOQSummary } from '../services/boqCalculationService';
import { db, supabase } from '../lib/supabase';
import { savePvDesign } from '../services/pvDesignPersistence';
import { autoUploadSolarDesignPdfsAfterSave } from '../services/solarDesignPdfAutoUpload';
import { insertDocumentWithOptionalFilesDualWrite } from '../services/projectDocuments';
import { Sun } from 'lucide-react';
import { getEffectiveConsumption } from '../utils/consumptionNormalization';

// Type-specific step configurations
const WORKFLOW_STEPS: Record<UserType, Array<{ id: Step; label: string }>> = {
  residential: [
    { id: 'type', label: 'Type' },
    { id: 'bill', label: 'Energy Profile' },
    { id: 'contact', label: 'Contact' },
    { id: 'verify', label: 'Verify' },
    { id: 'location', label: 'Design Requirements' },
    { id: 'solar-design', label: 'Solar Design' },
    { id: 'summary', label: 'Summary' },
    { id: 'confirmation', label: 'Confirmation' },
  ],
  commercial: [
    { id: 'type', label: 'Type' },
    { id: 'bill', label: 'Energy Profile' },
    { id: 'contact', label: 'Contact' },
    { id: 'verify', label: 'Verify' },
    { id: 'energy-consumption', label: 'Energy Consumption' },
    { id: 'site-operations', label: 'Site & Operations' },
    { id: 'location', label: 'Design Requirements' },
    { id: 'solar-design', label: 'Solar Design' },
    { id: 'summary', label: 'Summary' },
    { id: 'confirmation', label: 'Confirmation' },
  ],
  agricultural: [
    { id: 'type', label: 'Type' },
    { id: 'bill', label: 'Energy Profile' },
    { id: 'contact', label: 'Contact' },
    { id: 'verify', label: 'Verify' },
    { id: 'agri-design', label: 'Design Requirements' },
    { id: 'location', label: 'Location' },
    { id: 'solar-design', label: 'Solar Design' },
    { id: 'summary', label: 'Summary' },
    { id: 'confirmation', label: 'Confirmation' },
  ],
  industrial: [
    { id: 'type', label: 'Type' },
    { id: 'bill', label: 'Energy Profile' },
    { id: 'contact', label: 'Contact' },
    { id: 'verify', label: 'Verify' },
    { id: 'energy-consumption', label: 'Energy Consumption' },
    { id: 'site-operations', label: 'Site & Operations' },
    { id: 'location', label: 'Design Requirements' },
    { id: 'solar-design', label: 'Solar Design' },
    { id: 'summary', label: 'Summary' },
    { id: 'confirmation', label: 'Confirmation' },
  ],
};

// Helper function to get current workflow steps
const getCurrentSteps = (userType: UserType | null) => {
  return userType ? WORKFLOW_STEPS[userType] : WORKFLOW_STEPS.residential;
};

// Helper function to get workflow steps for logged-in users (skip contact/verify)
const getLoggedInUserSteps = (userType: UserType | null) => {
  const steps = getCurrentSteps(userType);
  return steps.filter(step => step.id !== 'contact' && step.id !== 'verify');
};

export function ProjectForm() {
  console.log('🚀 ProjectForm component is rendering!');
  
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [currentStep, setCurrentStep] = useState<Step>('type');
  const [isEditing, setIsEditing] = useState(false);
  const [existingProject, setExistingProject] = useState<any>(null);
  
  console.log('ProjectForm initialized with:', { id, isEditing, showWelcome: true, pathname: window.location.pathname });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showProjectLimitModal, setShowProjectLimitModal] = useState(false);
  const [showSystemSizeModal, setShowSystemSizeModal] = useState(false);
  const [showTypeChangeModal, setShowTypeChangeModal] = useState(false);
  const [pendingUserType, setPendingUserType] = useState<UserType | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [userType, setUserType] = useState<UserType | null>(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [billData, setBillData] = useState<BillData | null>(null);
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [energyConsumptionData, setEnergyConsumptionData] = useState<EnergyConsumptionData | null>(null);
  const [siteOperationsData, setSiteOperationsData] = useState<any>(null);
  const [contactData, setContactData] = useState<ContactInfo | null>(null);
  const [agriDesignData, setAgriDesignData] = useState<any>(null);
  const [solarDesign, setSolarDesign] = useState<SolarDesign | null>(null);
  const [financials, setFinancials] = useState<FinancialData | null>(null);
  const [boq, setBoq] = useState<BOQSummary | null>(null);
  const [pvDesignDbId, setPvDesignDbId] = useState<string | null>(null);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [latestProjectData, setLatestProjectData] = useState<any>(null); // Store latest project data
  const [formState, setFormState] = useState<{
    contactData?: ContactInfo;
    energyConsumptionData?: EnergyConsumptionData;
    siteOperationsData?: any;
  }>({});

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Processing...');
  const [showSuccess, setShowSuccess] = useState(false);

  const { user, checkAuth, isLoading: authLoading } = useAuthStore();
  const { currentTier } = useSubscriptionStore();

  // Check authentication on component mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Set auth ready when authentication check is complete
  useEffect(() => {
    if (!authLoading) {
      // Small delay to ensure state is settled
      const timer = setTimeout(() => {
        setAuthReady(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [authLoading]);

  // Set default tier for logged-in users
  useEffect(() => {
    if (user && !currentTier) {
      useSubscriptionStore.getState().setCurrentTier('free');
    }
  }, [user, currentTier]);


  const loadExistingProject = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data: project, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      setExistingProject(project);
      setIsEditing(true);
      setShowWelcome(false); // Skip welcome screen for existing projects
      
      // Set the user type and project ID
      setUserType(project.user_type);
      setCurrentProjectId(project.id);
      setPvDesignDbId(project.pv_design_id ?? null);
      
      // Load existing data into form state
      if (project.bill_data) {
        const bill = project.bill_data;
        setBillData({
          ...bill,
          monthlyUsage: bill.monthlyUsage ?? bill.monthly_usage ?? bill.monthly_usage_kwh ?? 0,
          monthlyBill: bill.monthlyBill ?? bill.monthly_bill ?? bill.monthly_bill_amount ?? 0,
          customTariffSarPerKwh:
            bill.customTariffSarPerKwh ??
            bill.custom_tariff_sar_per_kwh ??
            bill.custom_tariff_sar_kwh,
          yearlyConsumption: bill.yearlyConsumption ?? bill.yearly_consumption ?? 0,
          utilityProvider:
            bill.utilityProvider ??
            bill.utility_provider ??
            (Array.isArray(bill.utility_providers) && bill.utility_providers.length > 0 ? bill.utility_providers[0] : ''),
          morningConsumptionPct: bill.morningConsumptionPct ?? bill.morning_consumption_pct,
          effectiveYearlyConsumption: bill.effectiveYearlyConsumption ?? bill.effective_yearly_consumption
        });
      }
      if (project.location_data) {
        setLocationData(project.location_data);
      }
      
      // Determine the appropriate starting step
      if (project.project_status === 'completed') {
        setCurrentStep('summary');
      } else if (project.location_data) {
        setCurrentStep('summary');
      } else if (project.bill_data) {
        setCurrentStep('location');
      } else {
        setCurrentStep('bill');
      }
      
    } catch (error) {
      console.error('Error loading project:', error);
      alert('Failed to load project. Redirecting to new project.');
      navigate('/project/new');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);
  // Load existing project data if editing
  useEffect(() => {
    console.log('🔍 useEffect for loading project:', { id, pathname: window.location.pathname });
    
    // Check if this is a new project route (no id parameter)
    if (window.location.pathname === '/new-project' || window.location.pathname === '/project/new') {
      console.log('✅ New project route - showing welcome screen');
      setShowWelcome(true);
      setIsEditing(false);
      return;
    }
    
    // Only load existing project if we have an id and it's not 'new'
    if (id && id !== 'new') {
      console.log('📂 Loading existing project with id:', id);
      // Check if this is a details view
      if (window.location.pathname.includes('/details')) {
        // Handle details view - we'll show the details component
        loadExistingProject();
      } else {
        // Handle edit view
        loadExistingProject();
      }
    } else {
      console.log('✅ New project - not loading existing project');
    }
  }, [id, loadExistingProject]);

  // Helper function to fetch latest project data
  const fetchLatestProjectData = useCallback(async () => {
    if (user && currentProjectId) {
      try {
        const { data: project, error } = await supabase
          .from('projects')
          .select('solar_design, bill_data')
          .eq('id', currentProjectId)
          .single();

        if (error) {
          console.error('Error fetching latest project data:', error);
          return null;
        }

        setLatestProjectData(project);
        return project;
      } catch (error) {
        console.error('Error fetching latest project data:', error);
        return null;
      }
    }
    return null;
  }, [user, currentProjectId]);
  // Fetch latest project data when navigating to energy consumption step
  useEffect(() => {
    if (currentStep === 'energy-consumption') {
      fetchLatestProjectData();
    }
  }, [currentStep, fetchLatestProjectData]);

  // Helper function to show loading with success animation
  const showLoadingWithSuccess = async (message: string, successMessage: string, action: () => Promise<void>) => {
    setIsLoading(true);
    setLoadingMessage(message);
    setShowSuccess(false);
    
    try {
      await action();
      
      // Show success state
      setShowSuccess(true);
      setLoadingMessage(successMessage);
      
      // Wait a bit to show success, then hide loading
      setTimeout(() => {
        setIsLoading(false);
        setShowSuccess(false);
      }, 800);
    } catch (error) {
      setIsLoading(false);
      setShowSuccess(false);
      throw error;
    }
  };

  const handleBack = () => {
    // Use the correct step array based on user authentication status
    const currentSteps = user ? getLoggedInUserSteps(userType) : getCurrentSteps(userType);
    const currentIndex = currentSteps.findIndex(step => step.id === currentStep);
    
    if (currentIndex > 0) {
      const previousStep = currentSteps[currentIndex - 1].id;
      
      // Check if going back to type selection and there's existing data
      if (previousStep === 'type') {
        const hasExistingData = billData || locationData ||  energyConsumptionData || siteOperationsData ||
                               formState.contactData || formState.energyConsumptionData || formState.siteOperationsData;
        
        if (hasExistingData) {
          // Show confirmation dialog for going back to type selection
          setPendingUserType(null); // This will indicate going back to type selection
          setShowTypeChangeModal(true);
          return;
        }
      }
      
      // Go to previous step in the current workflow
      setCurrentStep(previousStep);
    }
  };

  const handleWelcomeNext = () => {
    console.log('🎯 Welcome screen next clicked - proceeding to user type selection');
    setShowWelcome(false);
    setCurrentStep('type'); // Proceed to user type selection
    console.log('🎯 State updated: showWelcome=false, currentStep=type');
  };

  const handleUserTypeSelect = async (type: UserType) => {
    if (user) {
      // Check project limit for logged-in users
      const canCreate = true; // Simplified for now
      if (!canCreate) {
        setShowProjectLimitModal(true);
        return;
      }
    }
    
    // Check if there's existing data that would be cleared
    const hasExistingData = billData || locationData ||  energyConsumptionData || siteOperationsData ||
                           formState.contactData || formState.energyConsumptionData || formState.siteOperationsData;
    
    if (hasExistingData && userType !== null) {
      // Show confirmation dialog
      setPendingUserType(type);
      setShowTypeChangeModal(true);
      return;
    }
    
    // Clear all form data when user type changes
    clearAllFormData();
    setUserType(type);
    setCurrentStep('bill');
  };

  const clearAllFormData = () => {
    setBillData(null);
    setLocationData(null);
    setEnergyConsumptionData(null);
    setSiteOperationsData(null);
    setCurrentProjectId(null);
    setPvDesignDbId(null);
    setLatestProjectData(null);
    setFormState({});
  };

  const confirmTypeChange = () => {
    clearAllFormData();
    
    if (pendingUserType) {
      // Changing to a new type
      setUserType(pendingUserType);
      setCurrentStep('bill');
    } else {
      // Going back to type selection
      setUserType(null);
      setCurrentStep('type');
    }
    
    setShowTypeChangeModal(false);
    setPendingUserType(null);
  };

  const cancelTypeChange = () => {
    setShowTypeChangeModal(false);
    setPendingUserType(null);
  };

  const handleBillData = async (data: BillData) => {
    setBillData(data);
    setFormState(prev => ({
      ...prev,
      energyProfileData: data
    }));
    
    // If user is already logged in, create or update project and continue to next step
    if (user) {
      await showLoadingWithSuccess(
        'Saving energy profile...',
        'Energy profile saved successfully!',
        async () => {
          const energyProfileData = {
            objectives: data?.objectives?.map(obj => obj.text) || [],
            energyMix: data?.energyMix || {}
          };
          const hasMorningPct =
            typeof data.morningConsumptionPct === 'number' && Number.isFinite(data.morningConsumptionPct);
          const normalizedMorningPct = hasMorningPct
            ? Math.max(0, Math.min(100, data.morningConsumptionPct!))
            : undefined;
          const normalizedCustomTariff =
            typeof data.customTariffSarPerKwh === 'number' &&
            Number.isFinite(data.customTariffSarPerKwh) &&
            data.customTariffSarPerKwh > 0
              ? data.customTariffSarPerKwh
              : undefined;
          const effectiveConsumption = getEffectiveConsumption(data, 0);

          // Check if project already exists
          if (currentProjectId) {
            // Update existing project
            
            const { data: existingProject, error: fetchError } = await supabase
              .from('projects')
              .select('solar_design, bill_data')
              .eq('id', currentProjectId)
              .single();

            if (fetchError) {
              console.error('Error fetching existing project:', fetchError);
              setLoginError('Failed to fetch existing project. Please try again.');
              return;
            }

            const existingSolarDesign = existingProject.solar_design || {};
            const updatedSolarDesign = {
              ...existingSolarDesign,
              objective: energyProfileData.objectives,
              energy_mix: energyProfileData.energyMix
            };

            // Prepare update data
            const updateData: any = {
              solar_design: updatedSolarDesign,
              updated_at: new Date().toISOString()
            };

            // Add bill data if available
            if (data.billDataForDB) {
              updateData.bill_data = data.billDataForDB;
            } else if (normalizedMorningPct != null || normalizedCustomTariff != null) {
              updateData.bill_data = {
                ...(existingProject.bill_data || {}),
                morning_consumption_pct: normalizedMorningPct,
                effective_yearly_consumption: effectiveConsumption.effectiveYearlyConsumption,
                custom_tariff_sar_per_kwh: normalizedCustomTariff
              };
            }

            const { error: updateError } = await supabase
              .from('projects')
              .update(updateData)
              .eq('id', currentProjectId);

            if (updateError) {
              console.error('Error updating project:', updateError);
              setLoginError('Failed to update project. Please try again.');
              return;
            }

          } else {
            // Create new project
            
            // Get user profile information
            const { data: userProfile, error: userError } = await supabase
              .from('users')
              .select('name, email, phone, account_type')
              .eq('id', user.id)
              .single();

            if (userError) {
              console.error('Error fetching user profile:', userError);
              setLoginError('Failed to fetch user information. Please try again.');
              return;
            }

            const projectData: any = {
              user_id: user.id,
              project_status: 'draft',
              user_type: userType || 'residential',
              solar_design: {
                objective: energyProfileData.objectives,
                energy_mix: energyProfileData.energyMix
              },
              contact_info: {
                name: userProfile.name,
                email: userProfile.email,
                phone: userProfile.phone,
                account_type: userProfile.account_type,
                company_name: '',
                cr_number: '',
                website_url: ''
              },
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };

            // Add bill data if available
            if (data.billDataForDB) {
              projectData.bill_data = data.billDataForDB;
            } else if (normalizedMorningPct != null || normalizedCustomTariff != null) {
              projectData.bill_data = {
                morning_consumption_pct: normalizedMorningPct,
                effective_yearly_consumption: effectiveConsumption.effectiveYearlyConsumption,
                custom_tariff_sar_per_kwh: normalizedCustomTariff
              };
            }

            const { data: projectResult, error: projectError } = await supabase
              .from('projects')
              .insert(projectData)
              .select()
              .single();

            if (projectError) {
              console.error('Error creating project:', projectError);
              setLoginError('Failed to create project. Please try again.');
              return;
            }

            setCurrentProjectId(projectResult.id);
          }

          // Get next step for logged-in users (skip contact/verify)
          const loggedInSteps = getLoggedInUserSteps(userType);
          const billIndex = loggedInSteps.findIndex(step => step.id === 'bill');
          const nextStep = loggedInSteps[billIndex + 1];
          
          console.log('🔍 Commercial workflow navigation debug:');
          console.log('User type:', userType);
          console.log('Logged in steps:', loggedInSteps);
          console.log('Bill index:', billIndex);
          console.log('Next step:', nextStep);
          
          if (nextStep) {
            setCurrentStep(nextStep.id);
          } else {
            console.error('No next step found after bill for commercial workflow');
            setCurrentStep('energy-consumption'); // Fallback
          }
        }
      );
    } else {
      // For new users, just store data and go to contact form
      setCurrentStep('contact');
    }
  };

  const handleContactSubmit = async (data: ContactInfo & { companyProfileFile?: File | null }) => {
    setContactData(data);
    setFormState(prev => ({
      ...prev,
      contactData: data
    }));

    // If user is already logged in, just continue to next step
    if (user) {
      const currentSteps = getCurrentSteps(userType);
      const contactIndex = currentSteps.findIndex(step => step.id === 'contact');
      const nextStep = currentSteps[contactIndex + 1];
      setCurrentStep(nextStep.id);
      return;
    }

    // For new users, create account and project with all collected data
    await showLoadingWithSuccess(
      'Creating your account...',
      'Account created successfully!',
      async () => {
      // Prepare energy profile data (send safe defaults in residential lead-gen)
      const energyProfileData = (userType === 'residential')
        ? {
            objectives: ['lead-generation'],
            energyMix: { grid: 100 }
          }
        : {
            objectives: billData?.objectives?.map(obj => obj.text) || [],
            energyMix: billData?.energyMix || {}
          };

        const effectiveConsumption = getEffectiveConsumption(billData, 0);
        const yearlyFromMonthly = effectiveConsumption.baseYearlyConsumption;
        const monthlyUsageFromMonthly = yearlyFromMonthly / 12;

        // Create project first (include bill data for lead-gen persistence)
        const billDataForDB = billData?.billDataForDB
          ? billData.billDataForDB
          : billData
            ? {
                electricity_bill: billData.uploadedBillUrl ?? null,
                monthly_usage: monthlyUsageFromMonthly ?? billData.monthlyUsage ?? 0,
                monthly_bill: billData.monthlyBill ?? 0,
                custom_tariff_sar_per_kwh:
                  typeof billData.customTariffSarPerKwh === 'number' &&
                  Number.isFinite(billData.customTariffSarPerKwh) &&
                  billData.customTariffSarPerKwh > 0
                    ? billData.customTariffSarPerKwh
                    : undefined,
                utility_provider: billData.utilityProvider ?? '',
                yearly_consumption: yearlyFromMonthly ?? billData.yearlyConsumption ?? (billData.monthlyUsage ? billData.monthlyUsage * 12 : 0),
                morning_consumption_pct: effectiveConsumption.morningConsumptionPct,
                effective_yearly_consumption: effectiveConsumption.effectiveYearlyConsumption,
                location_photos: billData.locationPhotos
              }
            : undefined;

      const result = await db.createUserAndProject(
        {
          name: data.name,
          email: data.email,
          phone: data.phone,
            password: data.password!,
          accountType: data.accountType,
          companyName: data.companyName,
          crNumber: data.crNumber,
          websiteUrl: data.websiteUrl
        },
        energyProfileData,
        userType || 'residential',
        billDataForDB
      );

        if (result.error) {
          throw result.error;
        }

        if (result.data) {
        setCurrentProjectId(result.data.projectId);
        
          setFormState(prev => ({
            ...prev,
            contactData: {
              name: data.name,
              email: data.email,
              phone: data.phone,
              accountType: data.accountType,
              companyName: data.companyName,
              crNumber: data.crNumber,
              websiteUrl: data.websiteUrl,
              password: data.password,
              verified: false
            }
          }));
        
        
          // Continue to next step (verification step)
        const currentSteps = getCurrentSteps(userType);
        const contactIndex = currentSteps.findIndex(step => step.id === 'contact');
        const nextStep = currentSteps[contactIndex + 1];
        setCurrentStep(nextStep.id);
      }
    }
    );
  };

  const handleVerificationComplete = async () => {
    await showLoadingWithSuccess(
      'Verifying your account...',
      'Account verified successfully!',
      async () => {
    // Check if the user is now logged in
    await checkAuth();
    
    // Navigate to next step in the current workflow
    const currentSteps = getCurrentSteps(userType);
    const verifyIndex = currentSteps.findIndex(step => step.id === 'verify');
    const nextStep = currentSteps[verifyIndex + 1];
    setCurrentStep(nextStep.id);
      }
    );
  };

  const handleLocationData = async (data: LocationData) => {
    // For backward compatibility, also set legacy fields if we have locations
    let compatibleData: LocationData = data;
    if (data.locations && data.locations.length > 0) {
      const primaryLocation = data.locations[0];
      compatibleData = {
        ...data,
        // Legacy fields for components that still expect them
        address: primaryLocation.address,
        latitude: primaryLocation.latitude,
        longitude: primaryLocation.longitude,
        areaInSqMeters: data.total_area_sqm,
        tilt: primaryLocation.tilt,
        azimuth: primaryLocation.azimuth,
        mountingType: primaryLocation.mounting_type,
        surfaceType: primaryLocation.surface_type
      } as LocationData;
    }
    
    setLocationData(compatibleData);
    setFormState(prev => ({
      ...prev,
      locationData: compatibleData
    }));

    await showLoadingWithSuccess(
      'Saving location data...',
      'Location data saved successfully!',
      async () => {
    // Save location data to database if user is logged in
        if (user && currentProjectId) {
          const { data: existingProject, error: fetchError } = await supabase
            .from('projects')
            .select('location_data, location_photos')
            .eq('id', currentProjectId)
            .single();

          if (fetchError) {
            console.error('Error fetching existing project data:', fetchError);
            return;
          }

          const existingLocationData = existingProject.location_data || {};
          
          // Handle both single location (backward compatibility) and multi-location data
          let locationDataUpdate;
          
          if (data.locations && data.locations.length > 0) {
            // Multi-location data - store only the locations array
            locationDataUpdate = {
              ...existingLocationData,
              // Store only the locations array for multi-location projects
              locations: data.locations,
              electrical: data.electrical ?? existingLocationData.electrical,
              // Preserve agricultural data if present
              agri_irrigation: data.agri_irrigation
            };
          } else {
            // Single location data (backward compatibility)
            locationDataUpdate = {
              ...existingLocationData,
              address: data.address,
              area_sqm: data.areaInSqMeters,
              tilt: data.tilt,
              azimuth: data.azimuth,
              mountingType: data.mountingType,
              electrical: data.electrical ?? existingLocationData.electrical,
              // Preserve agricultural data if present
              agri_irrigation: data.agri_irrigation
            };
          }

          let photoUrls: string[] = [];
          type PhotoDocMeta = { storagePath: string; publicUrl: string; fileName: string; mimeType: string; bytes: number };
          let photoDocMetas: PhotoDocMeta[] = [];
          if (data.photos && data.photos.length > 0) {
            try {
              const uploadPromises = data.photos.map(async (photo: File, index: number) => {
                const fileName = `location_photos/${currentProjectId}/${Date.now()}-${index}-${photo.name}`;

                const { error: uploadError } = await supabase.storage
                  .from('project_files')
                  .upload(fileName, photo);

                if (uploadError) {
                  console.error('Error uploading photo:', uploadError);
                  return null;
                }

                const { data: urlData } = supabase.storage
                  .from('project_files')
                  .getPublicUrl(fileName);

                return {
                  storagePath: fileName,
                  publicUrl: urlData.publicUrl,
                  fileName: photo.name,
                  mimeType: photo.type || 'image/*',
                  bytes: photo.size,
                } satisfies PhotoDocMeta;
              });

              const uploaded = await Promise.all(uploadPromises);
              const ok = uploaded.filter((u): u is PhotoDocMeta => u !== null);
              photoDocMetas = ok;
              photoUrls = ok.map((u) => u.publicUrl);
            } catch (error) {
              console.error('Error uploading photos:', error);
            }
          }

          const updates: any = {
            location_data: locationDataUpdate,
            updated_at: new Date().toISOString()
          };

          if (photoUrls.length > 0) {
            const existingPhotos = existingProject.location_photos || [];
            updates.location_photos = [...existingPhotos, ...photoUrls];
          }

          const { error: updateError } = await supabase
            .from('projects')
            .update(updates)
            .eq('id', currentProjectId);
          
          if (updateError) {
            console.error('Error updating project with location data:', updateError);
          } else if (photoDocMetas.length > 0) {
            for (const meta of photoDocMetas) {
              await insertDocumentWithOptionalFilesDualWrite({
                projectId: currentProjectId,
                designId: pvDesignDbId,
                docType: 'location_photo',
                storagePath: meta.storagePath,
                publicUrl: meta.publicUrl,
                fileName: meta.fileName,
                mimeType: meta.mimeType,
                bytes: meta.bytes,
                filesAppend: null,
              });
            }
          }
    }

    const currentSteps = getCurrentSteps(userType);
    const locationIndex = currentSteps.findIndex(step => step.id === 'location');
    const nextStep = currentSteps[locationIndex + 1];
    setCurrentStep(nextStep.id);
      }
    );
  };

  // Agricultural design requirements step handler (UI-only; no DB writes yet)
  const handleAgriDesignRequirements = async (partial: Partial<LocationData>) => {
    if (partial.agri_irrigation) {
      setAgriDesignData(partial.agri_irrigation);
    }
    
    setLocationData(prev => {
      const baseData = prev || {
        locations: [],
        total_area_sqm: 0,
        address: '', 
        latitude: 24.7136, 
        longitude: 46.6753, 
        areaInSqMeters: 0, 
        tilt: 10, 
        azimuth: 180,
        mountingType: 'roof-fixed', 
        surfaceType: 'asphalt'
      };
      return {
        ...baseData,
        ...(partial || {})
      } as LocationData;
    });

    // Fallback: estimate consumption from pump specs if the energy profile step
    // didn't collect actual consumption data.
    const currentYearly = billData?.yearlyConsumption || 0;
    const currentMonthlyArr = billData?.monthlyConsumptionArray;
    const hasConsumption = currentYearly > 0 ||
      (currentMonthlyArr && currentMonthlyArr.length === 12 && currentMonthlyArr.some(v => v > 0));

    if (!hasConsumption && partial.agri_irrigation) {
      const irr = partial.agri_irrigation;
      const PUMP_EFFICIENCY = 0.85;
      const HP_TO_KW = 0.746;
      const dailyHours = irr.helper_hours || 6;

      let totalDailyKwh = 0;
      if (irr.pumps && irr.pumps.length > 0) {
        for (const pump of irr.pumps) {
          if (pump.pump_category === 'mechanical' && pump.horsepower) {
            totalDailyKwh += (pump.horsepower * HP_TO_KW * dailyHours) / PUMP_EFFICIENCY;
          }
        }
      }

      if (totalDailyKwh > 0) {
        const yearlyEstimate = totalDailyKwh * 365;
        const monthlyEstimate = yearlyEstimate / 12;
        setBillData(prev => ({
          ...prev,
          type: prev?.type || 'bill',
          monthlyUsage: monthlyEstimate,
          yearlyConsumption: yearlyEstimate,
        }));
      }
    }

    const currentSteps = getCurrentSteps(userType);
    const idx = currentSteps.findIndex(step => step.id === 'agri-design');
    const nextStep = currentSteps[idx + 1];
    if (nextStep) setCurrentStep(nextStep.id);
  };


  // Solar design calculation handler
  const handleSolarDesign = (
    design: SolarDesign,
    financialsData: FinancialData,
    boqData: BOQSummary,
    extras: SolarDesignPersistExtras
  ) => {
    setSolarDesign(design);
    setFinancials(financialsData);
    setBoq(boqData);

    if (user && currentProjectId) {
      void (async () => {
        const id = await savePvDesign(currentProjectId, {
          design,
          financials: financialsData,
          boq: boqData,
          calculationResults: extras.calculationResults,
          calculationInputs: extras.calculationInputs,
          pricingFactors: extras.pricingFactors,
          moduleSnapshot: extras.moduleSnapshot,
          inverterSnapshot: extras.inverterSnapshot,
        });
        if (id) setPvDesignDbId(id);
        if (locationData) {
          await autoUploadSolarDesignPdfsAfterSave({
            projectId: currentProjectId,
            pvDesignId: id ?? null,
            design,
            financials: financialsData,
            boq: boqData,
            extras,
            location: locationData,
            billData: billData ?? undefined,
            userType: userType || 'residential',
          });
        }
      })();
    }

    // Navigate to next step (summary)
    const currentSteps = getCurrentSteps(userType);
    const idx = currentSteps.findIndex(step => step.id === 'solar-design');
    const nextStep = currentSteps[idx + 1];
    if (nextStep) setCurrentStep(nextStep.id);
  };

  const handleSummaryConfirm = async () => {
    await showLoadingWithSuccess(
      'Submitting your project...',
      'Project submitted successfully!',
      async () => {
        // Update project status to in-progress when user confirms summary
        if (user && currentProjectId) {
          const { error } = await supabase
            .from('projects')
            .update({
              project_status: 'in-progress',
              updated_at: new Date().toISOString()
            })
            .eq('id', currentProjectId);

          if (error) {
            console.error('Error updating project status to in-progress:', error);
            throw error;
          }
        }

        const currentSteps = getCurrentSteps(userType);
        const summaryIndex = currentSteps.findIndex(step => step.id === 'summary');
        const nextStep = currentSteps[summaryIndex + 1];
        setCurrentStep(nextStep.id);
      }
    );
  };



  const handleEnergyConsumptionData = async (data: EnergyConsumptionData) => {
    setEnergyConsumptionData(data);
    setFormState(prev => ({
      ...prev,
      energyConsumptionData: data
    }));

    await showLoadingWithSuccess(
      'Saving energy consumption data...',
      'Energy consumption data saved successfully!',
      async () => {
    // Save energy consumption data to database if user is logged in
        if (user && currentProjectId) {
          const { data: existingProject, error: fetchError } = await supabase
            .from('projects')
            .select('solar_design')
            .eq('id', currentProjectId)
            .single();

          if (fetchError) {
            console.error('Error fetching existing project data:', fetchError);
            return;
          }

          const existingSolarDesign = existingProject.solar_design || {};
          const updatedSolarDesign = {
            ...existingSolarDesign,
            usage_breakdown: data
          };

          const { error: updateError } = await supabase
            .from('projects')
            .update({ 
              solar_design: updatedSolarDesign,
              updated_at: new Date().toISOString()
            })
            .eq('id', currentProjectId);
          
          if (updateError) {
            console.error('Error updating project with usage breakdown:', updateError);
          }
    }

    const currentSteps = getCurrentSteps(userType);
    const energyIndex = currentSteps.findIndex(step => step.id === 'energy-consumption');
    const nextStep = currentSteps[energyIndex + 1];
    setCurrentStep(nextStep.id);
      }
    );
  };

  const handleSiteOperationsData = async (data: any) => {
    setSiteOperationsData(data);
    setFormState(prev => ({
      ...prev,
      siteOperationsData: data
    }));

    // Merge consumption into billData so SolarDesignView can read it
    setBillData(prev => ({
      ...prev,
      type: prev?.type || 'bill',
      monthlyConsumptionArray: data.monthlyConsumptionArray || prev?.monthlyConsumptionArray,
      monthlyUsage: data.monthlyUsage || prev?.monthlyUsage || 0,
      yearlyConsumption: data.yearlyConsumption || (data.monthlyUsage ? data.monthlyUsage * 12 : prev?.yearlyConsumption || 0),
    }));

    await showLoadingWithSuccess(
      'Saving site operations data...',
      'Site operations data saved successfully!',
      async () => {
    // Save site operations data to database if user is logged in
        if (user && currentProjectId) {
          const { data: existingProject, error: fetchError } = await supabase
            .from('projects')
            .select('bill_data, solar_design, files, contact_info')
            .eq('id', currentProjectId)
            .single();

          if (fetchError) {
            console.error('Error fetching existing project data:', fetchError);
            return;
          }

          const updates: any = {};

          if (data.monthlyUsage !== undefined || data.monthlyBill !== undefined || data.selectedUtilities) {
            const existingBillData = existingProject.bill_data || {};
            updates.bill_data = {
              ...existingBillData,
              monthly_usage_kwh: data.monthlyUsage,
              monthly_bill_amount: data.monthlyBill,
              monthly_bill_currency: 'SAR',
              utility_providers: data.selectedUtilities || []
            };
          }

          if (data.operatingHours) {
            const existingSolarDesign = existingProject.solar_design || {};
            updates.solar_design = {
              ...existingSolarDesign,
              operation_hours: {
                day: {
                  start: data.operatingHours.shift1?.start || '08:00',
                  end: data.operatingHours.shift1?.end || '16:00',
                  enabled: data.operatingHours.shift1?.enabled || false
                },
                evening: {
                  start: data.operatingHours.shift2?.start || '16:00',
                  end: data.operatingHours.shift2?.end || '00:00',
                  enabled: data.operatingHours.shift2?.enabled || false
                },
                night: {
                  start: data.operatingHours.shift3?.start || '00:00',
                  end: data.operatingHours.shift3?.end || '08:00',
                  enabled: data.operatingHours.shift3?.enabled || false
                },
                weekend_operations: data.operatingHours.weekends || false,
                holiday_operations: data.operatingHours.holidays || false
              }
            };
          }

        type SiteDocMeta = {
          docType: string;
          storagePath: string;
          publicUrl: string;
          fileName: string;
          mimeType: string;
          bytes: number;
          filesAppend?: { type: string; name: string; url: string } | null;
        };
        const siteDocMetas: SiteDocMeta[] = [];

        // Handle bill PDF uploads
        let billUrls: string[] = [];
        if (data.billFiles && data.billFiles.length > 0) {
          try {
            const uploadPromises = data.billFiles.map(async (file: File, index: number) => {
              const fileName = `bills/${currentProjectId}/${Date.now()}-${index}-${file.name}`;

              const { error: uploadError } = await supabase.storage
                .from('project_files')
                .upload(fileName, file);

              if (uploadError) {
                console.error('Error uploading bill PDF:', uploadError);
                return null;
              }

              const { data: urlData } = supabase.storage
                .from('project_files')
                .getPublicUrl(fileName);

              return {
                docType: 'bill',
                storagePath: fileName,
                publicUrl: urlData.publicUrl,
                fileName: file.name,
                mimeType: file.type || 'application/pdf',
                bytes: file.size,
                filesAppend: null,
              } satisfies SiteDocMeta;
            });

            const uploaded = await Promise.all(uploadPromises);
            const ok = uploaded.filter((u): u is SiteDocMeta => u !== null);
            siteDocMetas.push(...ok);
            billUrls = ok.map((u) => u.publicUrl);
          } catch (error) {
            console.error('Error uploading bill PDFs:', error);
          }
        }

        // Update bill_data with uploaded bill URLs
        if (billUrls.length > 0) {
          const existingBillData = existingProject.bill_data || {};
          updates.bill_data = {
            ...existingBillData,
            ...updates.bill_data,
            uploaded_bill_urls: billUrls
          };
        }

        // Handle datasheet and layout file uploads to blueprints bucket
        let datasheetUrls: string[] = [];
        let layoutUrls: string[] = [];
        
        if (data.datasheetFiles && data.datasheetFiles.length > 0) {
          try {
            
            const uploadPromises = data.datasheetFiles.map(async (file: File, index: number) => {
              const fileName = `blueprints/datasheets/${currentProjectId}/${Date.now()}-${index}-${file.name}`;

              const { error: uploadError } = await supabase.storage
                .from('project_files')
                .upload(fileName, file);

              if (uploadError) {
                console.error('Error uploading datasheet file:', uploadError);
                return null;
              }

              const { data: urlData } = supabase.storage
                .from('project_files')
                .getPublicUrl(fileName);

              const publicUrl = urlData.publicUrl;
              return {
                docType: 'datasheet',
                storagePath: fileName,
                publicUrl,
                fileName: file.name,
                mimeType: file.type || 'application/octet-stream',
                bytes: file.size,
                filesAppend: null,
              } satisfies SiteDocMeta;
            });

            const uploadedDs = await Promise.all(uploadPromises);
            const dsOk = uploadedDs.filter((u): u is SiteDocMeta => u !== null);
            siteDocMetas.push(...dsOk);
            datasheetUrls = dsOk.map((u) => u.publicUrl);
          } catch (error) {
            console.error('Error uploading datasheet files:', error);
          }
        }

        if (data.layoutFiles && data.layoutFiles.length > 0) {
          try {
            
            const uploadPromises = data.layoutFiles.map(async (file: File, index: number) => {
              const fileName = `blueprints/layouts/${currentProjectId}/${Date.now()}-${index}-${file.name}`;

              const { error: uploadError } = await supabase.storage
                .from('project_files')
                .upload(fileName, file);

              if (uploadError) {
                console.error('Error uploading layout file:', uploadError);
                return null;
              }

              const { data: urlData } = supabase.storage
                .from('project_files')
                .getPublicUrl(fileName);

              const publicUrl = urlData.publicUrl;
              return {
                docType: 'layout',
                storagePath: fileName,
                publicUrl,
                fileName: file.name,
                mimeType: file.type || 'application/octet-stream',
                bytes: file.size,
                filesAppend: null,
              } satisfies SiteDocMeta;
            });

            const uploadedLy = await Promise.all(uploadPromises);
            const lyOk = uploadedLy.filter((u): u is SiteDocMeta => u !== null);
            siteDocMetas.push(...lyOk);
            layoutUrls = lyOk.map((u) => u.publicUrl);
    } catch (error) {
            console.error('Error uploading layout files:', error);
          }
        }

        // Update files array with uploaded URLs instead of blob URLs
        const newFiles: any[] = [];
        
        // Add datasheet files with uploaded URLs
        if (datasheetUrls.length > 0) {
          data.datasheetFiles?.forEach((file: File, index: number) => {
            if (datasheetUrls[index]) {
              newFiles.push({
                type: 'machine_doc',
                name: file.name,
                url: datasheetUrls[index]
              });
            }
          });
        }

        // Add layout files with uploaded URLs
        if (layoutUrls.length > 0) {
          data.layoutFiles?.forEach((file: File, index: number) => {
            if (layoutUrls[index]) {
              newFiles.push({
                type: 'layout',
                name: file.name,
                url: layoutUrls[index]
              });
            }
          });
        }

        if (newFiles.length > 0) {
          const existingFiles = existingProject.files || [];
          updates.files = [...existingFiles, ...newFiles];
        }

        // Handle company profile file upload
        let companyProfileUrl = null;
        if (data.companyProfileFile && currentProjectId) {
          try {
            
            const fileName = `company_profiles/${currentProjectId}/${Date.now()}-${data.companyProfileFile.name}`;
            
            const { error: uploadError } = await supabase.storage
              .from('project_files')
              .upload(fileName, data.companyProfileFile);

            if (uploadError) {
              console.error('Error uploading company profile file:', uploadError);
            } else {
              const { data: urlData } = supabase.storage
                .from('project_files')
                .getPublicUrl(fileName);
              companyProfileUrl = urlData.publicUrl;

              siteDocMetas.push({
                docType: 'company_profile',
                storagePath: fileName,
                publicUrl: companyProfileUrl,
                fileName: data.companyProfileFile.name,
                mimeType: data.companyProfileFile.type || 'application/octet-stream',
                bytes: data.companyProfileFile.size,
                filesAppend: {
                  type: 'company_profile',
                  name: data.companyProfileFile.name,
                  url: companyProfileUrl,
                },
              });

              // Update contact_info with company profile URL
              const existingContactInfo = existingProject.contact_info || {};
              updates.contact_info = {
                ...existingContactInfo,
                company_profile_url: companyProfileUrl
              };
            }
    } catch (error) {
            console.error('Error uploading company profile file:', error);
          }
        }

          const { error: updateError } = await supabase
            .from('projects')
            .update({
              ...updates,
              updated_at: new Date().toISOString()
            })
            .eq('id', currentProjectId);
          
          if (updateError) {
            console.error('Error updating project with site operations data:', updateError);
          } else {
            for (const doc of siteDocMetas) {
              await insertDocumentWithOptionalFilesDualWrite({
                projectId: currentProjectId,
                designId: pvDesignDbId,
                docType: doc.docType,
                storagePath: doc.storagePath,
                publicUrl: doc.publicUrl,
                fileName: doc.fileName,
                mimeType: doc.mimeType,
                bytes: doc.bytes,
                filesAppend: doc.filesAppend ?? null,
              });
            }
          }
    }

    const currentSteps = getCurrentSteps(userType);
    const siteOpsIndex = currentSteps.findIndex(step => step.id === 'site-operations');
    const nextStep = currentSteps[siteOpsIndex + 1];
    setCurrentStep(nextStep.id);
      }
    );
  };

  const handleLoginSuccess = (data: ContactInfo) => {
    setContactData(data);
    setShowLoginModal(false);
    setLoginError(null);
  };

  const handleProjectLimitModalClose = () => {
    setShowProjectLimitModal(false);
    navigate('/dashboard');
  };

  const handleSystemSizeModalClose = () => {
    setShowSystemSizeModal(false);
  };

  // Show details view if this is a details route
  if (id && window.location.pathname.includes('/details') && existingProject) {
    return <ProjectDetailsView project={existingProject} />;
  }

  // Show welcome screen for new projects (explicit check for new project routes)
  if ((window.location.pathname === '/new-project' || window.location.pathname === '/project/new') && showWelcome) {
    console.log('✅ New project route - showing welcome screen immediately');
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="container mx-auto px-4 py-8">
          <WelcomeScreen onNext={handleWelcomeNext} />
        </div>
      </div>
    );
  }

  // Show welcome screen for new projects (fallback)
  if (showWelcome && !isEditing) {
    console.log('✅ Showing welcome screen', { showWelcome, isEditing, id, pathname: window.location.pathname });
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="container mx-auto px-4 py-8">
          <WelcomeScreen onNext={handleWelcomeNext} />
        </div>
      </div>
    );
  }

  // Show loading state if we're still loading, checking authentication, or auth is not ready
  if (isLoading || authLoading || !authReady) {
    return <LoadingOverlay message={authLoading ? 'Checking authentication...' : loadingMessage} isVisible={true} />;
  }

  // Debug: Log current state
  console.log('🎯 ProjectForm render state:', { 
    showWelcome, 
    isEditing, 
    id, 
    currentStep, 
    userType,
    isLoading,
    pathname: window.location.pathname 
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {isEditing && (
            <div className="mb-6">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Dashboard
              </button>
            </div>
          )}

          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <Sun className="h-8 w-8 text-yellow-500 mr-2" />
              <h1 className="text-3xl font-bold text-gray-900">
                {isEditing ? 'Edit Solar Project' : 'Solar Project Setup'}
              </h1>
            </div>
            <p className="text-gray-600">
              {isEditing 
                ? 'Continue working on your solar energy solution'
                : "Let's create your personalized solar energy solution"
              }
            </p>
          </div>

          {loginError && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center text-red-700">
                <div className="text-sm font-medium">{loginError}</div>
          <button
                  onClick={() => setLoginError(null)}
                  className="ml-auto text-red-500 hover:text-red-700"
          >
                  ×
          </button>
              </div>
        </div>
      )}

          {currentStep !== 'type' && authReady && (
            <StepIndicator 
              currentStep={currentStep} 
              steps={user ? getLoggedInUserSteps(userType) : getCurrentSteps(userType)} 
            />
          )}

          <div className="mt-8">
            {currentStep === 'type' && (
              <UserTypeSelector onSelect={handleUserTypeSelect} />
            )}

            {currentStep === 'bill' && (
              userType === 'agricultural' ? (
                <EnergyProfileAgricultural 
                  onNext={handleBillData} 
                  onBack={handleBack}
                  userType={userType}
                  initialData={billData}
                />
              ) : (
                <BillUpload 
                  onNext={handleBillData} 
                  onBack={handleBack}
                  userType={userType}
                  initialData={billData}
                  currentProjectId={currentProjectId}
                  user={user}
                />
              )
            )}

            {currentStep === 'contact' && !user && (
              <ContactForm 
                onSubmit={handleContactSubmit}
                onBack={handleBack}
                initialData={formState.contactData ? {
                  ...formState.contactData,
                  password: formState.contactData.password || '',
                  accountType: formState.contactData.accountType || 'individual',
                  websiteUrl: formState.contactData.websiteUrl || ''
                } : undefined}
                userType={userType}
              />
            )}

            {currentStep === 'verify' && !user && (
              <OTPVerification 
                contactInfo={formState.contactData as any}
                onVerified={handleVerificationComplete}
                onBack={handleBack}
                error={loginError}
              />
            )}

            {currentStep === 'location' && (
              <LocationSelector 
                onNext={handleLocationData} 
                onBack={handleBack}
                initialData={locationData || undefined}
                currentProjectId={currentProjectId}
                user={user}
                userType={userType}
              />
            )}


          {currentStep === 'agri-design' && (
            <AgriDesignRequirements
              onNext={handleAgriDesignRequirements}
              onBack={handleBack}
              initialData={locationData || undefined}
              currentProjectId={currentProjectId}
              user={user}
            />
          )}

          {currentStep === 'solar-design' && locationData && (
            <SolarDesignView
              location={locationData}
              billData={billData || undefined}
              userType={userType || 'residential'}
              projectId={currentProjectId}
              pvDesignId={pvDesignDbId}
              onNext={() => {
                const currentSteps = getCurrentSteps(userType);
                const idx = currentSteps.findIndex(step => step.id === 'solar-design');
                const nextStep = currentSteps[idx + 1];
                if (nextStep) setCurrentStep(nextStep.id);
              }}
              onBack={handleBack}
              onDesignCalculated={handleSolarDesign}
            />
          )}

            {currentStep === 'summary' && locationData && (
              <ProjectSummary 
                design={solarDesign || undefined} 
                financials={financials || undefined}
                boq={boq || undefined}
                location={locationData!}
                billData={billData}
                contactData={contactData}
                energyConsumptionData={energyConsumptionData}
                siteOperationsData={siteOperationsData}
                agriDesignData={agriDesignData}
                userType={userType || undefined}
                persistPdfProjectId={currentProjectId}
                persistPdfDesignId={pvDesignDbId}
                onBack={() => setCurrentStep('location')}
                onConfirm={handleSummaryConfirm}
              />
            )}

            {currentStep === 'energy-consumption' && (userType === 'industrial' || userType === 'commercial') && (
              <EnergyConsumption
                onNext={handleEnergyConsumptionData}
                onBack={handleBack}
                initialData={energyConsumptionData || undefined}
                energyMixData={latestProjectData?.solar_design?.energy_mix || billData?.energyMix}
                energySourcesData={billData?.energySources}
              />
            )}

            {currentStep === 'site-operations' && (userType === 'industrial' || userType === 'commercial') && (
              <SiteOperations
                onNext={handleSiteOperationsData}
                onBack={handleBack}
                initialData={siteOperationsData}
              />
            )}

            {currentStep === 'confirmation' && (
              <ConfirmationScreen 
                contactInfo={formState.contactData || null}
                locationData={locationData || undefined}
                onViewDashboard={() => navigate('/dashboard')}
                onSiteVisitScheduled={async () => {
                  // Handle site visit scheduling
                  return true;
                }}
                existingProjectId={currentProjectId || undefined}
              />
            )}
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      <LoadingOverlay 
        isVisible={isLoading}
        message={loadingMessage}
        showSuccess={showSuccess}
      />

      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onLogin={handleLoginSuccess}
          error={loginError}
          onStartNewProject={() => window.location.href = '/new-project'}
        />
      )}

        <ProjectLimitModal
          isOpen={showProjectLimitModal}
        onClose={handleProjectLimitModalClose}
        currentTier={currentTier || 'free'}
        maxProjects={5}
      />

        <SystemSizeLimitModal
          isOpen={showSystemSizeModal}
        onClose={handleSystemSizeModalClose}
        currentTier={currentTier || 'free'}
        systemSizeLimit={100}
        currentSize={0}
      />

      {/* Type Change Confirmation Modal */}
      {showTypeChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  {pendingUserType ? "Change Project Type" : "Return to Type Selection"}
                </h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                {pendingUserType 
                  ? "Changing the project type will clear all your current form data and start fresh."
                  : "Returning to project type selection will clear all your current form data and start fresh."
                }
                This includes your energy profile, location data, design requirements, and any other information you've entered.
              </p>
              <p className="text-sm text-gray-600 mt-2 font-medium">
                Are you sure you want to continue?
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelTypeChange}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmTypeChange}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Yes, Clear Data & Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
